/**
 * Utility functions for UDE metric filters
 * Handles unit conversion for metric filter values
 */
import { getMeasurementMetric, getMeasurementSync } from '../query/measurements';
import { Measurement, MeasurementMetric, MetricFilter } from '../query/query.types';

/**
 * Unit option with display info and conversion multiplier
 */
export interface UnitOption {
  value: string;
  label: string;
  multiplier: number;
}

/**
 * Unit options by storage unit type
 * Used for both UI display and value conversion
 */
const DURATION_UNIT_OPTIONS: UnitOption[] = [
  { value: 'ms', label: 'ms (milliseconds)', multiplier: 1000 },
  { value: 's', label: 's (seconds)', multiplier: 1000000 },
  { value: 'min', label: 'min (minutes)', multiplier: 60000000 }
];

const BITS_PER_SECOND_UNIT_OPTIONS: UnitOption[] = [
  { value: 'bps', label: 'bps', multiplier: 1 },
  { value: 'Kbps', label: 'Kbps', multiplier: 1000 },
  { value: 'Mbps', label: 'Mbps', multiplier: 1000000 },
  { value: 'Gbps', label: 'Gbps', multiplier: 1000000000 }
];

const MEGABITS_PER_SECOND_UNIT_OPTIONS: UnitOption[] = [
  { value: 'Mbps', label: 'Mbps', multiplier: 1 },
  { value: 'Gbps', label: 'Gbps', multiplier: 1000 },
  { value: 'bps', label: 'bps', multiplier: 0.000001 }
];

const BYTES_UNIT_OPTIONS: UnitOption[] = [
  { value: 'B', label: 'B (bytes)', multiplier: 1 },
  { value: 'KB', label: 'KB', multiplier: 1024 },
  { value: 'MB', label: 'MB', multiplier: 1048576 },
  { value: 'GB', label: 'GB', multiplier: 1073741824 }
];

const PERCENT_UNIT_OPTIONS: UnitOption[] = [{ value: '%', label: '% (percent)', multiplier: 1 }];

const PACKETS_UNIT_OPTIONS: UnitOption[] = [{ value: 'pps', label: 'packets/s', multiplier: 1 }];

const HEALTH_UNIT_OPTIONS: UnitOption[] = [{ value: '', label: '(health score)', multiplier: 1 }];

const DNS_CODE_UNIT_OPTIONS: UnitOption[] = [{ value: '', label: '(DNS code)', multiplier: 1 }];

const HTTP_STATUS_UNIT_OPTIONS: UnitOption[] = [{ value: '', label: '(HTTP status)', multiplier: 1 }];

const RAW_UNIT_OPTIONS: UnitOption[] = [{ value: '', label: '(raw value)', multiplier: 1 }];

/** Packets-per-second: single static label, no dropdown needed. Rollup value IS in packets/s. */
const PACKETS_PER_SECOND_UNIT_OPTIONS: UnitOption[] = [{ value: 'pps', label: 'packets/s', multiplier: 1 }];

/** Flows-per-second: single static label, no dropdown needed. Rollup value IS in flows/s. */
const FLOWS_PER_SECOND_UNIT_OPTIONS: UnitOption[] = [{ value: 'fps', label: 'flows/s', multiplier: 1 }];

/** Duration stored in seconds */
const SECONDS_UNIT_OPTIONS: UnitOption[] = [
  { value: 'ms', label: 'ms (milliseconds)', multiplier: 0.001 },
  { value: 's', label: 's (seconds)', multiplier: 1 },
  { value: 'min', label: 'min (minutes)', multiplier: 60 }
];

/** Duration stored in milliseconds */
const MILLISECONDS_UNIT_OPTIONS: UnitOption[] = [
  { value: 'ms', label: 'ms (milliseconds)', multiplier: 1 },
  { value: 's', label: 's (seconds)', multiplier: 1000 },
  { value: 'min', label: 'min (minutes)', multiplier: 60000 }
];

/** Duration stored in minutes */
const MINUTES_UNIT_OPTIONS: UnitOption[] = [
  { value: 'min', label: 'min (minutes)', multiplier: 1 },
  { value: 's', label: 's (seconds)', multiplier: 1 / 60 },
  { value: 'hr', label: 'hr (hours)', multiplier: 60 }
];

/** Bytes per second rate */
const BYTES_PER_SECOND_UNIT_OPTIONS: UnitOption[] = [
  { value: 'B/s', label: 'B/s (bytes/sec)', multiplier: 1 },
  { value: 'KB/s', label: 'KB/s', multiplier: 1024 },
  { value: 'MB/s', label: 'MB/s', multiplier: 1048576 },
  { value: 'GB/s', label: 'GB/s', multiplier: 1073741824 }
];

/** Temperature in Celsius — descriptive label, no conversion */
const CELSIUS_UNIT_OPTIONS: UnitOption[] = [{ value: '°C', label: '°C (Celsius)', multiplier: 1 }];

/** Connection counts — descriptive label, no conversion */
const CONNECTIONS_UNIT_OPTIONS: UnitOption[] = [{ value: '', label: 'connections', multiplier: 1 }];

/** Units that represent categorical/discrete values — no numeric conversion needed */
const CATEGORICAL_UNITS = new Set(['health', 'dns_code', 'http_status_code']);

/**
 * Infer a unit string from MeasurementMetric properties when Unit is not explicitly set.
 * Used to provide unit dropdowns for popular traffic metrics that lack a Unit property.
 *
 * Only infers for well-understood patterns:
 * - ToBits + rate → bits/s (e.g., both_bits_per_sec)
 * - rate + packets column → packets/s (e.g., both_pkts_per_sec)
 * - rate + trautocount alias → flows/s (e.g., fps)
 *
 * Returns undefined for metrics where inference isn't safe.
 */
export function inferUnit(metricInfo: MeasurementMetric | undefined): string | undefined {
  if (!metricInfo || metricInfo.Unit) {
    return metricInfo?.Unit;
  }

  // ToBits + rate → bits/s (the SQL does * 8 / i_duration, producing bits/s)
  if (metricInfo.ToBits && metricInfo.Window === 'rate') {
    return 'bits/s';
  }

  // Rate metrics on packet columns → packets/s
  if (metricInfo.Window === 'rate' && metricInfo.Column && /pkts?$/i.test(metricInfo.Column)) {
    return 'packets/s';
  }

  // fps metric: rate on trautocount (i_count) → flows/s
  if (metricInfo.Window === 'rate' && metricInfo.Alias === 'trautocount') {
    return 'flows/s';
  }

  return undefined;
}

/**
 * Get unit display options based on metric's Unit property
 * Returns options for the unit suffix dropdown (e.g., 'ms', 's', 'min')
 */
export function getUnitOptions(metricInfo: MeasurementMetric | undefined): UnitOption[] {
  const unit = metricInfo?.Unit || inferUnit(metricInfo);

  if (!unit) {
    return RAW_UNIT_OPTIONS;
  }

  // Duration — microsecond base (existing + aliases)
  if (unit === 'duration:microseconds' || unit === 'microseconds' || unit === 'microsecond' || unit === 'duration') {
    return DURATION_UNIT_OPTIONS;
  }

  // Duration — second base (NMS)
  if (unit === 's' || unit === 'second' || unit === 'seconds') {
    return SECONDS_UNIT_OPTIONS;
  }

  // Duration — millisecond base (NMS)
  if (unit === 'milliseconds') {
    return MILLISECONDS_UNIT_OPTIONS;
  }

  // Duration — minute base (NMS)
  if (unit === 'minutes') {
    return MINUTES_UNIT_OPTIONS;
  }

  // Percentage
  if (unit === 'percent' || unit === '%') {
    return PERCENT_UNIT_OPTIONS;
  }

  // Megabits per second (storage unit is Mbps)
  if (unit === 'Mbits/s' || unit === 'Mbps') {
    return MEGABITS_PER_SECOND_UNIT_OPTIONS;
  }

  // Bits per second (storage unit is bps + NMS alias)
  if (unit === 'bits/s' || unit === 'bps' || unit === 'bits/sec') {
    return BITS_PER_SECOND_UNIT_OPTIONS;
  }

  // Bytes
  if (unit === 'bytes' || unit === 'B') {
    return BYTES_UNIT_OPTIONS;
  }

  // Bytes per second (NMS)
  if (unit === 'bytes/second') {
    return BYTES_PER_SECOND_UNIT_OPTIONS;
  }

  // Packets (no conversion needed)
  if (unit === 'packets' || unit === 'pps') {
    return PACKETS_UNIT_OPTIONS;
  }

  // Health score (0-100, no unit)
  if (unit === 'health') {
    return HEALTH_UNIT_OPTIONS;
  }

  // DNS response codes
  if (unit === 'dns_code') {
    return DNS_CODE_UNIT_OPTIONS;
  }

  // HTTP status codes
  if (unit === 'http_status_code') {
    return HTTP_STATUS_UNIT_OPTIONS;
  }

  // Packets per second (inferred from Window: 'rate' + pkts column + NMS alias)
  if (unit === 'packets/s' || unit === 'packets/sec') {
    return PACKETS_PER_SECOND_UNIT_OPTIONS;
  }

  // Flows per second (inferred from Window: 'rate' + trautocount alias)
  if (unit === 'flows/s') {
    return FLOWS_PER_SECOND_UNIT_OPTIONS;
  }

  // Temperature (NMS)
  if (unit === 'celsius') {
    return CELSIUS_UNIT_OPTIONS;
  }

  // Connections (NMS)
  if (unit === 'connections') {
    return CONNECTIONS_UNIT_OPTIONS;
  }

  // Default - no unit conversion
  return RAW_UNIT_OPTIONS;
}

/**
 * Get unit options for a storage unit string (without MeasurementMetric)
 */
function getUnitOptionsForStorageUnit(storageUnit: string): UnitOption[] {
  if (
    storageUnit === 'duration:microseconds' ||
    storageUnit === 'microseconds' ||
    storageUnit === 'microsecond' ||
    storageUnit === 'duration'
  ) {
    return DURATION_UNIT_OPTIONS;
  }
  if (storageUnit === 's' || storageUnit === 'second' || storageUnit === 'seconds') {
    return SECONDS_UNIT_OPTIONS;
  }
  if (storageUnit === 'milliseconds') {
    return MILLISECONDS_UNIT_OPTIONS;
  }
  if (storageUnit === 'minutes') {
    return MINUTES_UNIT_OPTIONS;
  }
  if (storageUnit === 'bits/s' || storageUnit === 'bps' || storageUnit === 'bits/sec') {
    return BITS_PER_SECOND_UNIT_OPTIONS;
  }
  if (storageUnit === 'Mbits/s' || storageUnit === 'Mbps') {
    return MEGABITS_PER_SECOND_UNIT_OPTIONS;
  }
  if (storageUnit === 'bytes' || storageUnit === 'B') {
    return BYTES_UNIT_OPTIONS;
  }
  if (storageUnit === 'bytes/second') {
    return BYTES_PER_SECOND_UNIT_OPTIONS;
  }
  if (storageUnit === 'percent' || storageUnit === '%') {
    return PERCENT_UNIT_OPTIONS;
  }
  if (storageUnit === 'packets' || storageUnit === 'pps') {
    return PACKETS_UNIT_OPTIONS;
  }
  if (storageUnit === 'packets/s' || storageUnit === 'packets/sec') {
    return PACKETS_PER_SECOND_UNIT_OPTIONS;
  }
  if (storageUnit === 'flows/s') {
    return FLOWS_PER_SECOND_UNIT_OPTIONS;
  }
  if (storageUnit === 'celsius') {
    return CELSIUS_UNIT_OPTIONS;
  }
  if (storageUnit === 'connections') {
    return CONNECTIONS_UNIT_OPTIONS;
  }
  return RAW_UNIT_OPTIONS;
}

/**
 * Get unit multiplier for a given storage unit type and display unit
 */
function getMultiplierForUnit(storageUnit: string, displayUnit: string): number {
  // Get the options for this storage unit type
  const options = getUnitOptionsForStorageUnit(storageUnit);
  const option = options.find((opt) => opt.value === displayUnit);
  return option?.multiplier ?? 1;
}

/**
 * Parse a filter value that may contain a unit suffix
 * Format: "123|unit" where unit is optional
 */
export function parseFilterValue(value: string): { numericValue: string; unit: string } {
  if (!value) {
    return { numericValue: '', unit: '' };
  }
  const parts = value.split('|');
  return {
    numericValue: parts[0] || '',
    unit: parts[1] || ''
  };
}

/**
 * Combine numeric value and unit for storage
 */
export function combineFilterValue(numericValue: string, unit: string): string {
  if (!numericValue) {
    return '';
  }
  return unit ? `${numericValue}|${unit}` : numericValue;
}

/**
 * Convert a display value to storage value based on metric unit type
 * @param displayValue - The value entered by the user (e.g., "100")
 * @param displayUnit - The unit selected by the user (e.g., "ms")
 * @param storageUnit - The metric's storage unit (e.g., "duration:microseconds")
 * @returns The value converted to storage units (e.g., "100000")
 */
export function convertToStorageValue(
  displayValue: string,
  displayUnit: string,
  storageUnit: string | undefined
): string {
  if (!displayValue || !storageUnit) {
    return displayValue;
  }

  const numericValue = parseFloat(displayValue);
  if (Number.isNaN(numericValue)) {
    return displayValue;
  }

  const multiplier = getMultiplierForUnit(storageUnit, displayUnit);
  const convertedValue = numericValue * multiplier;

  // Return as integer if result is a whole number
  return Number.isInteger(convertedValue) ? String(convertedValue) : convertedValue.toFixed(6).replace(/\.?0+$/, '');
}

/**
 * Convert a storage value to display value based on metric unit type
 * @param storageValue - The value in storage units (e.g., "100000")
 * @param displayUnit - The unit to display (e.g., "ms")
 * @param storageUnit - The metric's storage unit (e.g., "duration:microseconds")
 * @returns The value in display units (e.g., "100")
 */
export function convertToDisplayValue(
  storageValue: string,
  displayUnit: string,
  storageUnit: string | undefined
): string {
  if (!storageValue || !storageUnit) {
    return storageValue;
  }

  const numericValue = parseFloat(storageValue);
  if (Number.isNaN(numericValue)) {
    return storageValue;
  }

  const multiplier = getMultiplierForUnit(storageUnit, displayUnit);
  if (multiplier === 0) {
    return storageValue;
  }

  const convertedValue = numericValue / multiplier;

  // Return as integer if result is a whole number
  return Number.isInteger(convertedValue) ? String(convertedValue) : convertedValue.toFixed(6).replace(/\.?0+$/, '');
}

/**
 * Process metric filters before sending to backend
 * Converts display values to storage values
 */
export function processMetricFiltersForQuery(
  filters: MetricFilter[],
  measurementName: string,
  measurements?: Measurement[]
): MetricFilter[] {
  const measurementObj = getMeasurementSync(measurementName, measurements);
  if (!measurementObj) {
    return filters;
  }

  return filters.map((filter) => {
    const { numericValue, unit } = parseFilterValue(filter.filterValue);
    if (!numericValue || !filter.metric) {
      return filter;
    }

    const metricInfo = getMeasurementMetric(measurementObj, filter.metric);
    const storageUnit = metricInfo?.Unit || inferUnit(metricInfo ?? undefined);

    // Skip conversion for categorical units — values are already in storage format
    // (e.g., health: '200', dns_code: '0', http_status_code: '200' or '2xx')
    if (storageUnit && CATEGORICAL_UNITS.has(storageUnit)) {
      return filter;
    }

    const convertedValue = convertToStorageValue(numericValue, unit, storageUnit);

    return {
      ...filter,
      filterValue: convertedValue
    };
  });
}

/**
 * Get the default display unit for a metric based on its storage unit type
 */
export function getDefaultDisplayUnit(metricInfo: MeasurementMetric | undefined): string {
  const unit = metricInfo?.Unit || inferUnit(metricInfo);

  if (!unit) {
    return '';
  }

  // Duration — microsecond base (existing + aliases)
  if (unit === 'duration:microseconds' || unit === 'microseconds' || unit === 'microsecond' || unit === 'duration') {
    return 'ms';
  }

  // Duration — second base (NMS)
  if (unit === 's' || unit === 'second' || unit === 'seconds') {
    return 's';
  }

  // Duration — millisecond base (NMS)
  if (unit === 'milliseconds') {
    return 'ms';
  }

  // Duration — minute base (NMS)
  if (unit === 'minutes') {
    return 'min';
  }

  // Bits per second (existing + NMS alias)
  if (unit === 'bits/s' || unit === 'bps' || unit === 'bits/sec') {
    return 'Mbps';
  }

  // Megabits per second
  if (unit === 'Mbits/s' || unit === 'Mbps') {
    return 'Mbps';
  }

  // Bytes
  if (unit === 'bytes' || unit === 'B') {
    return 'KB';
  }

  // Bytes per second (NMS)
  if (unit === 'bytes/second') {
    return 'KB/s';
  }

  // Percent
  if (unit === 'percent' || unit === '%') {
    return '%';
  }

  // Packets
  if (unit === 'packets' || unit === 'pps') {
    return 'pps';
  }

  // Packets per second (existing + NMS alias)
  if (unit === 'packets/s' || unit === 'packets/sec') {
    return 'pps';
  }

  // Flows per second
  if (unit === 'flows/s') {
    return 'fps';
  }

  // Temperature (NMS)
  if (unit === 'celsius') {
    return '°C';
  }

  // Connections (NMS)
  if (unit === 'connections') {
    return '';
  }

  return '';
}
